import {FC, useCallback, useEffect, useState, TouchEvent as ReactTouchEvent, useRef} from "react";
import Box from "@mui/material/Box";
import {lighten, styled} from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import client from "../lib/api.ts";
import UploadButton from "./Carousel/UploadButton";
import DeleteButton from "./Carousel/DeleteButton";
import MoveLeftButton from "./Carousel/MoveLeftButton";
import MoveRightButton from "./Carousel/MoveRightButton";
import SizeObservableBox from "@memaro/components/SizeObservableBox.tsx";
import Section, {SectionProps} from "../base/Section.tsx";
import {useFeedback} from "@memaro/feedback";
import ButtonOverlay from "@memaro/components/ButtonOverlay.tsx";
import LoadingOverlay from "@memaro/components/LoadingOverlay.tsx";

export type CarouselSectionProps = Omit<SectionProps, 'padding'> & {
    profileID: string
    photos: string[]
    onChange: (sectionID: string, photos: string[]) => void
}

type ContainerProps = {
    height: number
}

const Container = styled(SizeObservableBox)<ContainerProps>(({height}) => ({
    position: "relative",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    height,
    overflow: 'hidden',

    ':hover .overlay': {
        opacity: 0.9,
    }
}))

const IconButtonLeft = styled(IconButton)({
    zIndex: 10,
    position: 'absolute',
    left: 10
})

const IconButtonRight = styled(IconButton)({
    zIndex: 10,
    position: 'absolute',
    right: 10
})

const Placeholder = styled(Box)(({theme}) => ({
    display: "flex",
    backgroundColor: lighten(theme.palette.primary.main, 0.4),
    height: "100%",
    width: "100%",
}))

type ImageContainerProps = {
    width: number
}

const ImageContainer = styled(Box)<ImageContainerProps>(({width}) => ({
    width,
    height: "100%",
    img: {
        width: '100%',
        height: '100%',
        objectFit: 'contain'
    }
}))

type SlidesContainerProps = {
    width: number,
    offset: number,
    swiping: boolean
}

const SlidesContainer = styled(Box)<SlidesContainerProps>(({width, offset, swiping}) => ({
    width,
    height: "100%",
    display: 'flex',
    flexDirection: 'row',
    transform: `translateX(-${offset}px)`,
    transition: swiping ? "" : "transform 0.5s ease-in-out",
}))

type Point = {
    x: number
    y: number
}

const CarouselSection: FC<CarouselSectionProps> = ({profileID, sectionID, photos, onChange, editMode, ...sectionProps}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [current, setCurrent] = useState<number>(0);
    const slidesContainerRef = useRef<HTMLDivElement>(null)
    const swipeStart = useRef<Point | null>(null);
    const swipeCurrent = useRef<Point | null>(null);
    const [swipeOffset, setSwipeOffset] = useState<number>(0);
    const [containerWidth, setContainerWidth] = useState(0);
    const {error} = useFeedback()

    const handleNext = useCallback(() => setCurrent(prev => Math.min(prev + 1, photos.length - 1)), [photos.length]);
    const handlePrev = useCallback(() => setCurrent(prev => Math.max(prev - 1, 0)), []);

    const calculateSwipeOffset = useCallback(() => {
        if (swipeStart.current === null || swipeCurrent.current === null) {
            return 0
        }
        const dy = swipeStart.current.y - swipeCurrent.current.y
        const dx = swipeStart.current.x - swipeCurrent.current.x
        if (Math.abs(dy) > Math.abs(dx)) {
            // swiping vertically
            return 0
        }
        return dx
    }, [])

    const updateSwipeOffset = useCallback(() => {
        const offset = calculateSwipeOffset()
        setSwipeOffset(offset)
        return offset
    }, [calculateSwipeOffset])

    const handleTouchEnd = useCallback(() => {
        if (calculateSwipeOffset() > 10) {
            handleNext()
        }
        if (-calculateSwipeOffset() > 10) {
            handlePrev()
        }
        swipeStart.current = null
        swipeCurrent.current = null
        updateSwipeOffset()
    }, [calculateSwipeOffset, handleNext, handlePrev, updateSwipeOffset])

    const handleTouchStart = useCallback((event: ReactTouchEvent<HTMLDivElement>) => {
        swipeStart.current = {
            x: event.touches[0].clientX,
            y: event.touches[0].clientY
        }
        swipeCurrent.current = swipeStart.current
        updateSwipeOffset()
    }, [updateSwipeOffset])

    const handleTouchMove = useCallback((event: TouchEvent) => {
        if (swipeStart.current !== null) {
            swipeCurrent.current = {
                x: event.touches[0].clientX,
                y: event.touches[0].clientY
            }
            if (updateSwipeOffset() != 0) {
                event.preventDefault()
            }
        }
    }, [updateSwipeOffset])

    useEffect(() => {
        if (current > photos.length - 1) {
            setCurrent(Math.max(0, photos.length - 1))
        }
        window.addEventListener("touchmove", handleTouchMove, {passive: false})
        window.addEventListener("touchend", handleTouchEnd)

        return () => {
            window.addEventListener("touchmove", handleTouchMove, {passive: false})
            window.addEventListener("touchend", handleTouchEnd)
        }
    }, [photos, current, handleTouchStart, handleTouchMove, handleTouchEnd])

    const sliderWidth = photos.length * containerWidth
    const sliderOffset = Math.min(Math.max(current * containerWidth + swipeOffset, 0), sliderWidth - containerWidth)

    const handleFilesSelected = useCallback((files: File[]) => {
        setLoading(true)
        client.uploadMulti(profileID, "carousel", files)
            .then((m) => onChange(sectionID, [...photos, ...m.values()]))
            .catch(() => error?.("Er is een fout opgetreden bij het uploaden van de foto's"))
            .finally(() => setLoading(false))
    }, [profileID, onChange, sectionID, photos, error])

    const handleDelete = useCallback((index: number) => {
        const newPhotos = [...photos]
        newPhotos.splice(index, 1)
        onChange(sectionID, newPhotos)
    }, [sectionID, photos, onChange])

    const handleMoveLeft = useCallback((index: number) => {
        const newPhotos = [...photos]
        newPhotos[index] = photos[index - 1]
        newPhotos[index - 1] = photos[index]
        onChange(sectionID, newPhotos)
        handlePrev()
    }, [photos, onChange, sectionID, handlePrev])

    const handleMoveRight = useCallback((index: number) => {
        const newPhotos = [...photos]
        newPhotos[index] = photos[index + 1]
        newPhotos[index + 1] = photos[index]
        onChange(sectionID, newPhotos)
        handleNext()
    }, [photos, onChange, sectionID, handleNext])

    return (
        <Section {...sectionProps} editMode={editMode} sectionID={sectionID} padding={0}>
            <Container onSizeChange={setContainerWidth} className="carousel" height={containerWidth * 9 / 16}>
                <IconButtonLeft onClick={handlePrev} disabled={loading || current == 0 || photos.length < 2}>
                    <NavigateBeforeIcon/>
                </IconButtonLeft>
                {photos.length == 0 ? (
                    <Placeholder/>
                ) : (
                    <SlidesContainer
                        ref={slidesContainerRef}
                        width={sliderWidth}
                        offset={sliderOffset}
                        onTouchStart={handleTouchStart}
                        swiping={Boolean(swipeOffset)}
                    >
                        {photos.map((photo, index) => (
                            <ImageContainer key={`card-${index}`} width={containerWidth}>
                                <img src={photo} alt={photo}/>
                            </ImageContainer>
                        ))}
                    </SlidesContainer>
                )}
                {editMode ? (
                    <ButtonOverlay opacity={photos.length == 0 || loading ? 0.9 : 0}>
                        <MoveLeftButton
                            index={current}
                            onClick={handleMoveLeft}
                            disabled={loading || photos.length == 0 || current == 0}
                        />
                        <UploadButton onFilesSelected={handleFilesSelected} disabled={loading}/>
                        <DeleteButton
                            index={current}
                            onClick={handleDelete}
                            disabled={loading || photos.length == 0}
                        />
                        <MoveRightButton
                            index={current}
                            onClick={handleMoveRight}
                            disabled={loading || photos.length == 0 || current == photos.length - 1}
                        />
                    </ButtonOverlay>
                ) : null}
                <IconButtonRight onClick={handleNext}
                                 disabled={loading || current == photos.length - 1 || photos.length < 2}>
                    <NavigateNextIcon/>
                </IconButtonRight>
                <LoadingOverlay opacity={loading ? 1 : 0} />
            </Container>
        </Section>
    )
}

export default CarouselSection
